import { useEffect, useRef, useState } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import type { GetConversationFiltersQuery } from 'generated/graphql';
import {
    CONVERSATION_FILTERS_API_ROUTE,
    type loader,
} from '~/routes/remix-api.chat.conversation-filters';
import { useUserLoggedIn } from '~/modules/user';

const conversationFiltersCacheAtom = atom<{
    projects: GetConversationFiltersQuery['getConversationFilters']['Projects'];
} | null>(null);

/**
 * Hook to get the conversation filters to filter the chat inbox conversations.
 */
export function useConversationFilters() {
    const { isLoggedIn } = useUserLoggedIn();
    const [cache, setCache] = useAtom(conversationFiltersCacheAtom);
    const isFetched = useRef(false);
    const fetcher = useFetcher<typeof loader>();
    const [filters, setFilters] = useState<SerializeFrom<typeof loader>>(cache);

    useEffect(() => {
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(CONVERSATION_FILTERS_API_ROUTE);
            isFetched.current = true;
        } else if (fetcher.data && fetcher.state === 'idle') {
            setFilters(fetcher.data);
            setCache(fetcher.data);
        }
    }, [fetcher, setCache]);

    // Clear the conversation filters cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setCache(null);
            fetcher.data = undefined;
            isFetched.current = false;
        }
    }, [isLoggedIn, fetcher, setCache]);

    return {
        filters,
        isLoading: fetcher.state !== 'idle',
    };
}
